/*eslint-disable*/

import React, { useState, useEffect } from 'react';
import { Grid, Container, Typography, Box, Button, CircularProgress, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useQueryClient } from 'react-query';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';

import toast from 'react-hot-toast';

// import { CheckinCheckout } from '../components/checkin';
import { getUserDetails } from '../../utils';
import {
  useQueryGetPairedUsersAdmin,
  useMutationEndTraining,
  useMutationEndPairing,
} from '../../hooks/react-query/pairing';


import { TableView } from '../../components/table';
import CreatePairDialog from '../../components/dialog/CreatePairDialog';
import PairCard from '../../components/pairing/PairCard';
import PairingTable from '../../components/pairing/PairingTable';

const FindAnEdifier = () => {
  const queryClient = useQueryClient();

  const userDetails = getUserDetails();

  const userToken = userDetails?.token;

  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [createPairOpen, setCreatePairOpen] = useState(false);
  const [searchedValue, setSearchedValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'table'

  const [trainingEndingPayload, setTrainingEndingPayload] = useState({});

  const [pairingEndingPayload, setPairingEndingPayload] = useState({});

  // Removed pagination state since we're showing all pairs

  const { data: pairedUsersData, isFetching: isLoadingPairs } = useQueryGetPairedUsersAdmin(
    userToken, 
    navigate, 
    toast,
    1, // Always get first page
    1000, // Get a large number to show all pairs
    searchQuery
  );
  const { isLoading: isEndingTraining, mutate: endTraining } = useMutationEndTraining(
    userToken,
    navigate,
    toast,
    trainingEndingPayload,
    setTrainingEndingPayload,
    queryClient
  );

  const { isLoading: isEndingPairing, mutate: endPairing } = useMutationEndPairing(
    userToken,
    navigate,
    toast,
    pairingEndingPayload,
    setPairingEndingPayload,
    queryClient
  );

  // Remove client-side data processing since we're using server-side now

  useEffect(() => {
    if (Object.keys(trainingEndingPayload).length > 0) {
      endTraining();
    }
  }, [trainingEndingPayload]);

  useEffect(() => {
    if (Object.keys(pairingEndingPayload).length > 0) {
      endPairing();
    }
  }, [pairingEndingPayload]);

  // Server-side search function
  const searchFromRecord = () => {
    setSearchQuery(searchedValue);
  };

  const clearSearch = () => {
    setSearchedValue('');
    setSearchQuery('');
  };

  return (
    <Box>
      <Helmet>
        <title>Edifying Teachers | Manage Pairings</title>
      </Helmet>

      <Container maxWidth="xl" sx={{ padding: '0 !important' }}>
        <Box>
          <Typography variant="h3" sx={{ mb: '5px', color: 'text.titles' }}>
            Manage Pairings
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            mt: '23.63px',
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', md: 'row' },
            rowGap: '20px',
          }}
        >
          <Box sx={{ 
            display: 'flex',
            columnGap: '17px',
            flexDirection: { xs: 'column', md: 'row' },
            rowGap: '20px',
            justifyContent: 'space-between',
            width: '100%',
            alignItems: { xs: 'stretch', md: 'center' }
          }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField
                placeholder="Search Pairs by Name"
                className="filters"
                size="small"
                value={searchedValue}
                sx={{ 
                  minWidth: '300px', 
                  maxWidth: { xs: '100%', md: '300px' },
                  '& .MuiInputBase-root': {
                    height: '40px'
                  },
                  '& .MuiInputBase-input': {
                    height: '40px',
                    py: 0
                  }
                }}
                onChange={(e) => setSearchedValue(e.target.value.toLowerCase())}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    searchFromRecord();
                  }
                }}
              />

              <Button
                variant="contained"
                color="main"
                size="small"
                onClick={() => searchFromRecord()}
                sx={{ 
                  width: 'max-content',
                  height: '40px',
                  minHeight: '40px',
                  maxHeight: '40px',
                  lineHeight: 1,
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                Search
              </Button>

              {(searchQuery || searchedValue) && (
                <Button
                  variant="outlined"
                  color="secondary"
                  size="small"
                  onClick={clearSearch}
                  sx={{ 
                    width: 'max-content',
                    height: '40px',
                    minHeight: '40px',
                    maxHeight: '40px',
                    lineHeight: 1,
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  Clear
                </Button>
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: { xs: 'space-between', md: 'flex-end' } }}>
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(e, newMode) => newMode && setViewMode(newMode)}
                aria-label="view mode"
                size="small"
                sx={{
                  height: '40px',
                  '& .MuiToggleButton-root': {
                    px: 2,
                    border: '1px solid rgba(86, 156, 211, 0.3)',
                    '&.Mui-selected': {
                      background: 'linear-gradient(135deg, #569cd3 0%, #7DB8E5 100%)',
                      color: 'white',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #3A7BA8 0%, #569cd3 100%)',
                      },
                    },
                  },
                }}
              >
                <ToggleButton value="cards" aria-label="card view">
                  <ViewModuleIcon sx={{ mr: 0.5 }} />
                  Cards
                </ToggleButton>
                <ToggleButton value="table" aria-label="table view">
                  <ViewListIcon sx={{ mr: 0.5 }} />
                  Table
                </ToggleButton>
              </ToggleButtonGroup>

              <Button
                variant="contained"
                color="main"
                sx={{ height: '40px', width: 'max-content' }}
                onClick={() => setCreatePairOpen(true)}
              >
                Create Pair
              </Button>
            </Box>
          </Box>
        </Box>

        {isLoadingPairs && (
          <Box sx={{ textAlign: 'center', mt: '30px' }}>
            <CircularProgress />
          </Box>
        )}

        {!isLoadingPairs && pairedUsersData?.data && pairedUsersData.data.length > 0 ? (
          <>
            {viewMode === 'cards' ? (
              <Grid container spacing={2.5} sx={{ mt: 1 }}>
                {pairedUsersData.data.map((pair, index) => (
                  <Grid item xs={12} sm={6} md={4} xl={3} key={pair._id}>
                    <PairCard
                      pairData={pair}
                      isEndingTraining={isEndingTraining}
                      isEndingPairing={isEndingPairing}
                      setTrainingEndingPayload={setTrainingEndingPayload}
                      setPairingEndingPayload={setPairingEndingPayload}
                      index={index}
                    />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <PairingTable
                tableData={pairedUsersData?.data || []}
                isEndingTraining={isEndingTraining}
                isEndingPairing={isEndingPairing}
                setTrainingEndingPayload={setTrainingEndingPayload}
                setPairingEndingPayload={setPairingEndingPayload}
              />
            )}
          </>
        ) : !isLoadingPairs && (
          <Box sx={{ textAlign: 'center', mt: 5, py: 8 }}>
            <Typography variant="h4" sx={{ color: 'text.secondary', mb: 2 }}>
              No Pairings Found
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              {searchQuery ? 'Try adjusting your search criteria' : 'Create your first pair to get started'}
            </Typography>
          </Box>
        )}

        <CreatePairDialog
          open={createPairOpen}
          handleClose={() => setCreatePairOpen(false)}
          userToken={userToken}
          navigate={navigate}
          toast={toast}
          queryClient={queryClient}
          refetchPairedUsers={() => {
            // Refetch paired users data after successful pair creation
            queryClient.invalidateQueries({ queryKey: ['getPairedUsersAdmin'] });
          }}
        />
      </Container>
    </Box>
  );
};

export default FindAnEdifier;
